/*!
 * Copyright (c) 2026 Elevation Technologies Pte Ltd.
 * Developer: filimoni@elevationtech.com.fj
 */
import React, { useState } from 'react';
import ContactIcon from '../components/ContactIcon';
import PhotoLightbox from '../components/PhotoLightbox';
import { CSR_DOCUMENT_PAGES, CSR_FOCUS_AREAS, CSR_POLICY_URL, HOME_IMAGES } from '../data/siteData';

export function CsrDocumentPage({ page, index, onOpen }) {
  return (
    <figure className="fsf-csr-document-page">
      <button
        className="fsf-csr-document-page__button"
        type="button"
        onClick={() => onOpen(index)}
        aria-label={`Open CSR policy page ${index + 1}`}
      >
        <img src={page.src} alt={page.alt} loading={index === 0 ? 'eager' : 'lazy'} />
      </button>
      <figcaption>Page {index + 1}</figcaption>
    </figure>
  );
}

export default function CorporateSocialResponsibilityPageSections() {
  const [activePageIndex, setActivePageIndex] = useState(null);

  return (
    <>
      <div className="fsf-csr-story">
        <section className="fsf-csr-section fsf-csr-section--intro">
          <div className="fsf-csr-section__inner fsf-csr-intro">
            <div className="fsf-csr-intro__copy">
              <p className="fsf-section-kicker">Corporate Social Responsibility</p>
              <h2>Delivering with purpose, powered by responsibility.</h2>
              <p>
                Freight Services Fiji is committed to sustainable development and responsible logistics. Our CSR policy
                guides the way we care for our environment, support our communities, and make decisions that protect the
                future of the Pacific.
              </p>
              <div className="fsf-csr-actions">
                <a className="fsf-button fsf-button--sustainability" href={CSR_POLICY_URL} target="_blank" rel="noopener noreferrer">
                  View CSR Policy
                </a>
                <a className="fsf-button fsf-button--secondary" href="/contactus/">
                  Contact FSF
                </a>
              </div>
            </div>

            <figure className="fsf-csr-hero-card">
              <img src={HOME_IMAGES.truck} alt="Freight Services Fiji sustainability truck artwork" loading="eager" />
            </figure>
          </div>
        </section>

        <section className="fsf-csr-section fsf-csr-section--focus">
          <div className="fsf-csr-section__inner">
            <div className="fsf-csr-section__header">
              <p className="fsf-section-kicker">Our Focus</p>
              <h2>Responsible logistics with people and place in mind</h2>
            </div>
            <div className="fsf-csr-focus-grid">
              {CSR_FOCUS_AREAS.map((area) => (
                <article className="fsf-csr-focus-card" key={area.title}>
                  <span className="fsf-csr-focus-card__icon" aria-hidden="true">
                    <ContactIcon name={area.icon} />
                  </span>
                  <h3>{area.title}</h3>
                  <p>{area.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="fsf-csr-section fsf-csr-section--document">
          <div className="fsf-csr-section__inner">
            <div className="fsf-csr-document-header">
              <div>
                <p className="fsf-section-kicker">CSR Statement</p>
                <h2>Read the full policy document</h2>
                <p>
                  These pages outline FSF's commitments to sustainable communities, environmental responsibility, and
                  long-term social impact.
                </p>
              </div>
              <a className="fsf-text-link" href={CSR_POLICY_URL} target="_blank" rel="noopener noreferrer">
                Open PDF
              </a>
            </div>

            <div className="fsf-csr-document-grid">
              {CSR_DOCUMENT_PAGES.map((documentPage, index) => (
                <CsrDocumentPage
                  key={documentPage.src}
                  page={documentPage}
                  index={index}
                  onOpen={setActivePageIndex}
                />
              ))}
            </div>
          </div>
        </section>
      </div>

      <PhotoLightbox
        images={CSR_DOCUMENT_PAGES}
        activeIndex={activePageIndex}
        setActiveIndex={setActivePageIndex}
        label="Freight Services Fiji CSR policy pages"
      />
    </>
  );
}
